import { auth0ApiClient } from './auth0ApiClient'
import {
  AvailableMentor,
  Relationship,
  MatchingRequest,
  CreateMatchingRequestData,
  MentorSearchFilters,
  SuggestedMentee
} from '@/types/matching'

export interface SubmitRatingData {
  rating: number
  review?: string
}

class MatchingService {
  private readonly baseUrl = '/matching'

  /**
   * Get available mentors (for mentees)
   */
  async getAvailableMentors(filters?: MentorSearchFilters): Promise<AvailableMentor[]> {
    const queryString = filters ? this.buildQueryString(filters) : ''
    return auth0ApiClient.get<AvailableMentor[]>(`${this.baseUrl}/available-mentors${queryString}`)
  }

  /**
   * Request a mentor (mentee only)
   */
  async requestMentor(data: CreateMatchingRequestData): Promise<MatchingRequest> {
    return auth0ApiClient.post<MatchingRequest>(`${this.baseUrl}/request-mentor`, data)
  }

  /**
   * Accept a mentee request (mentor only)
   */
  async acceptMentee(requestId: string): Promise<Relationship> {
    return auth0ApiClient.post<Relationship>(`${this.baseUrl}/accept-mentee`, { requestId })
  }

  /**
   * Decline a mentee request (mentor only)
   */
  async declineMentee(requestId: string): Promise<void> {
    return auth0ApiClient.post<void>(`${this.baseUrl}/decline-mentee`, { requestId })
  }

  /**
   * Get user's relationships (both mentor and mentee)
   */
  async getMyRelationships(): Promise<Relationship[]> {
    return auth0ApiClient.get<Relationship[]>(`${this.baseUrl}/my-relationships`)
  }

  /**
   * Get pending requests for mentor
   */
  async getPendingRequests(): Promise<MatchingRequest[]> {
    return auth0ApiClient.get<MatchingRequest[]>(`${this.baseUrl}/pending-requests`)
  }

  /**
   * Get mentees suggested for the requesting mentor
   */
  async getSuggestedMentees(): Promise<SuggestedMentee[]> {
    return auth0ApiClient.get<SuggestedMentee[]>(`${this.baseUrl}/suggested-mentees`)
  }

  /**
   * Get mentors suggested for the requesting mentee
   */
  async getSuggestedMentors(): Promise<AvailableMentor[]> {
    return auth0ApiClient.get<AvailableMentor[]>(`${this.baseUrl}/suggested-mentors`)
  }

  /**
   * Submit a 1-5 star rating (with optional review) for the counterpart on a
   * completed relationship. A participant may only rate once per relationship.
   */
  async rateRelationship(relationshipId: string, data: SubmitRatingData): Promise<Relationship> {
    return auth0ApiClient.post<Relationship>(`${this.baseUrl}/relationships/${relationshipId}/rate`, data)
  }

  /**
   * Mark a relationship as completed. Requires both parties to have rated
   * first - throws with code RATINGS_INCOMPLETE (409) otherwise.
   */
  async completeRelationship(relationshipId: string): Promise<Relationship> {
    return auth0ApiClient.post<Relationship>(`${this.baseUrl}/relationships/${relationshipId}/complete`, {})
  }

  /**
   * Build query string from search parameters
   */
  private buildQueryString(params: MentorSearchFilters): string {
    const searchParams = new URLSearchParams()

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach((v) => searchParams.append(key, v.toString()))
        } else {
          searchParams.append(key, value.toString())
        }
      }
    })

    const queryString = searchParams.toString()
    return queryString ? `?${queryString}` : ''
  }
}

export const matchingService = new MatchingService()
